/**
 * PraticaTimelineItem — F-005 Phase 4 (IMPL-P4-004).
 *
 * Singolo `<li>` della timeline pratica. Usa `<time dateTime>` per
 * l'accessibilità (lettori automatici / SR leggono il timestamp ISO,
 * mentre la UI mostra il formato locale italiano).
 *
 * Etichette `EVENT_LABELS` e `ACTOR_LABELS` sono local: la enum eventType
 * è gestita server-side come stringa (vedi Phase 1) — qui mappiamo solo i
 * valori noti, fallback al codice raw per forward compatibility.
 *
 * NB: il progetto NON ha `date-fns` installato. Usiamo `Intl.DateTimeFormat`
 * con locale `it-IT` — output equivalente, zero deps.
 */
import type { PraticaDetailResponse } from '../lib/cliente-pratiche-client';

const ACTOR_LABELS: Record<string, string> = {
  CLIENT: 'Cliente',
  OPERATOR: 'Operatore',
  SYSTEM: 'Sistema',
};

const EVENT_LABELS: Record<string, string> = {
  BOZZA_CREATED: 'Pratica creata',
  SUBMITTED: 'Pratica inviata',
  TAKEN: 'Presa in carico',
  RELEASED: 'Rilasciata',
  BLOCKED: 'Bloccata',
  CORRECTION_DRAFT_SAVED: 'Bozza correzione salvata',
  RESUBMITTED: 'Correzione inviata',
  COMPLETED: 'Completata',
  ARCHIVED: 'Archiviata',
};

const DATE_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatItalianDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Esempio output: "7 mag 2026, 14:30" — formato canonico Intl IT-IT.
  return DATE_FORMATTER.format(d);
}

interface PraticaTimelineItemProps {
  event: PraticaDetailResponse['timeline'][number];
}

export function PraticaTimelineItem({ event }: PraticaTimelineItemProps) {
  const actor = ACTOR_LABELS[event.actorType] ?? event.actorType;
  const label = EVENT_LABELS[event.eventType] ?? event.eventType;
  const dateStr = formatItalianDate(event.createdAt);

  return (
    <li className="relative ml-2">
      <span
        className="absolute -left-[1.55rem] mt-1.5 h-3 w-3 rounded-full border border-white bg-gray-300"
        aria-hidden="true"
      />
      <time
        dateTime={event.createdAt}
        className="text-xs text-gray-500"
      >
        {dateStr}
      </time>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="text-xs text-gray-500">
        {actor}
        {event.actorName ? ` — ${event.actorName}` : ''}
      </p>
    </li>
  );
}
